import React, { useEffect, useState } from 'react';
import { Info } from 'lucide-react';
import api from 'config/axiosConfig';
import { useUser } from 'util/UserContext';
import InputField from 'ui/InputField';
import { UsernameValidationResponse } from 'util/constants';
import AuthService from 'services/authService';

interface LocalSigninProps {
  onClose: () => void;
  onForgotPassword: (email: string) => void;
  onUseCode: () => void;
  onRegistrationSuccess: (email: string) => void;
  initialEmail?: string;
  renderButtons: (props: {
    isSubmitting: boolean;
    isRegistering: boolean;
    isButtonDisabled: boolean;
    onSubmit: (e: React.FormEvent) => Promise<void>;
  }) => React.ReactNode;
}

const PASSWORD_REQUIREMENTS = {
  minLength: 8,
  maxLength: 100,
};

const USERNAME_REQUIREMENTS = {
  minLength: 2,
  maxLength: 30,
};

const RESTRICTED_USERNAME_LIST = [
  'user',
  'societea',
  'societea.io',
  'www.societea.io',
  'societea.com',
  'societea.app',
  'www.societea.app',
  'societeaapp',
  'societeainc.com',
  'societeainc',
  'societea.inc',
  'admin',
  'administrator',
  'deleted'
];

const LocalSignin: React.FC<LocalSigninProps> = ({
  onClose,
  onForgotPassword,
  onUseCode,
  onRegistrationSuccess,
  initialEmail = "",
  renderButtons,
}) => {
  const { refreshUser } = useUser();
  const [isRegistering, setIsRegistering] = useState(false);
  const [email, setEmail] = useState(initialEmail);
  const [emailOrUsername, setEmailOrUsername] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState<boolean>(false);
  const [isValidUsername, setIsValidUsername] = useState<boolean>(false);
  const [validationMessage, setValidationMessage] = useState<string>('');
  const [isPrivate, setIsPrivate] = useState<boolean>(false);

  // PKCE state
  const [codeVerifier, setCodeVerifier] = useState<string>('');
  const [codeChallenge, setCodeChallenge] = useState<string>('');

  // Generate PKCE parameters on mount or when isRegistering changes.
  useEffect(() => {
    const generateRandomString = (length: number): string => {
      const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
      let result = '';
      const values = new Uint8Array(length);
      window.crypto.getRandomValues(values);
      for (let i = 0; i < length; i++) {
        result += charset[values[i] % charset.length];
      }
      return result;
    };

    const generateCodeChallenge = async (verifier: string): Promise<string> => {
      const encoder = new TextEncoder();
      const data = encoder.encode(verifier);
      const digest = await window.crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(digest));
      const base64String = btoa(String.fromCharCode(...hashArray))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
      return base64String;
    };

    const initPKCE = async () => {
      const verifier = generateRandomString(64);
      const challenge = await generateCodeChallenge(verifier);
      setCodeVerifier(verifier);
      setCodeChallenge(challenge);
    };

    initPKCE();
  }, [isRegistering]);

  useEffect(() => {
    if (initialEmail) {
      setEmail(initialEmail);
      setEmailOrUsername(initialEmail);
      setIsEmailValid(true);
    }
  }, [initialEmail]);

  // Validate username on change.
  const validateUsername = async (username: string): Promise<void> => {
    setIsValidUsername(true);
    setValidationMessage('');
    if (
      /^[a-z0-9_.]{2,30}$/.test(username) &&
      !/\.{2,}/.test(username) &&
      !username.startsWith(".") &&
      !username.startsWith("_") &&
      !username.endsWith(".") &&
      !username.endsWith("_") &&
      !username.includes("..") &&
      !username.includes("__") &&
      !/^admin/.test(username) &&
      !/admin$/.test(username) &&
      !RESTRICTED_USERNAME_LIST.includes(username)
    ) {
      try {
        const response = await api.get<UsernameValidationResponse>(`/api/user/${username}`);
        if (response.status === 404) {
          setIsValidUsername(true);
          setValidationMessage('');
        } else {
          setIsValidUsername(false);
          setValidationMessage('Username is already taken.');
        }
      } catch (error: any) {
        if (error.response?.status === 404) {
          setIsValidUsername(true);
          setValidationMessage('');
        } else {
          setIsValidUsername(false);
          setValidationMessage('Username is already taken.');
        }
      }
    } else {
      if (username.startsWith(".") || username.startsWith("_") || username.endsWith(".") || username.endsWith("_")) {
        setIsValidUsername(false);
        setValidationMessage(`Username must not start/end with '.' or '_'`);
      } else if (/^admin/.test(username) || /admin$/.test(username) || RESTRICTED_USERNAME_LIST.includes(username)) {
        setIsValidUsername(false);
        setValidationMessage('Username is not allowed.');
      } else if (!/^[a-z0-9_.]{2,30}$/.test(username)) {
        setIsValidUsername(false);
        setValidationMessage(`Username may not contain special characters other than '.' and '_'`);
      } else if (username.includes("..") || username.includes("__")) {
        setIsValidUsername(false);
        setValidationMessage(`Username may not contain consecutive '.' or '_'`);
      }
    }
  };

  useEffect(() => {
    if (username && username.length > 1) {
      validateUsername(username);
    } else if (username && (username.startsWith(".") || username.startsWith("_"))) {
      setIsValidUsername(false);
      setValidationMessage(`Username must not start with '.' or '_'`);
    } else if (username && username.length < USERNAME_REQUIREMENTS.minLength) {
      setIsValidUsername(false);
      setValidationMessage(`Username must be at least ${USERNAME_REQUIREMENTS.minLength} characters long.`);
    } else {
      setValidationMessage('');
    }
  }, [username]);

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setUsername(e.target.value.toLowerCase());
  };

  const validatePassword = (password: string): string | null => {
    if (password.length < PASSWORD_REQUIREMENTS.minLength) {
      return 'Password must be at least 8 characters long';
    }
    if (password.length > PASSWORD_REQUIREMENTS.maxLength) {
      return 'Password must not exceed 100 characters';
    }
    return null;
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const emailValue = e.target.value;
    setEmail(emailValue);
    setIsEmailValid(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(emailValue));
  };

  const handleEmailOrUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmailOrUsername(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      if (isRegistering) {
        const passwordError = validatePassword(password);
        if (passwordError) {
          setError(passwordError);
          setIsSubmitting(false);
          return;
        }
        const response = await api.post('/auth/register', {
          email,
          password,
          username,
          isPrivate,
          code_verifier: codeVerifier,
          code_challenge: codeChallenge
        });
        if (response.status === 201) {
          // Store the access token if present
          if (response.data.accessToken) {
            AuthService.setAccessToken(response.data.accessToken);
          }
          onRegistrationSuccess(email);
        }
      } else {
        const response = await api.post('/auth/login', {
          emailOrUsername,
          password,
          code_verifier: codeVerifier,
          code_challenge: codeChallenge
        });
        if (response.status === 200) {
          // Store the access token
          if (response.data.accessToken) {
            AuthService.setAccessToken(response.data.accessToken);
          }
          await refreshUser();
          onClose();
        }
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isButtonDisabled =
    ((isRegistering && (!username || !isValidUsername)) || 
     (isRegistering && (!email || !isEmailValid)) || 
     (!isRegistering && emailOrUsername.length < 2) || 
     isSubmitting || 
     password.length < PASSWORD_REQUIREMENTS.minLength);

  return (
    <div className="flex flex-col flex-grow space-y-4">
      <div className="relative flex flex-row gap-2 items-center">
        <div className="w-full border-t border-eoe-border-main dark:border-eoe-dark-border-main" />
        <div className="relative flex flex-row flex-nowrap flex-shrink-0 justify-center text-sm">
          <span className="bg-eoe-bg dark:bg-eoe-dark-bg-modal text-eoe-text-sub dark:text-eoe-dark-text-sub">
            OR
          </span>
        </div>
        <div className="w-full border-t border-eoe-border-main dark:border-eoe-dark-border-main" />
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col flex-grow space-y-4">
        <div className="form-control mb-0">
          {isRegistering ? (
          <InputField
            type="email"
            id="email"
            name="email"
            labelText="Email"
            autoComplete="email"
            fieldRequired
            value={emailOrUsername.includes('@') ? emailOrUsername : email}
            onChange={handleEmailChange}
            validationState={isEmailValid ? 'valid' : email ? 'invalid' : 'none'}
            spellCheck={false}
          />
            ): (
              <InputField
              type="text"
              id="emailOrUsername"
              name="emailOrUsername"
              labelText="Email or username"
              autoComplete="username"
              fieldRequired
              value={emailOrUsername}
              onChange={handleEmailOrUsernameChange}
              validationState={emailOrUsername.length >= 2 ? 'valid' : emailOrUsername.length > 0 ? 'invalid' : 'none'}
              spellCheck={false}
          />)}
        </div>

        <div className="form-control">
          <InputField
            type="password"
            id="password"
            name="password"
            labelText="Password"
            autoComplete={isRegistering ? 'new-password' : 'current-password'}
            fieldRequired
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            validationState={isRegistering && password.length >= PASSWORD_REQUIREMENTS.minLength ? 'valid' : !isRegistering ? 'none' : 'invalid'}
            spellCheck={false}
            maxLength={PASSWORD_REQUIREMENTS.maxLength}
            minLength={PASSWORD_REQUIREMENTS.minLength}
          />
        </div>

        <div className="flex flex-col space-y-4 justify-start items-start">
          <div className="grid grid-flow-col-dense gap-3 items-center justify-between w-48">
            <label htmlFor="register" className="col-span-2 block font-semibold text-sm text-eoe-text-heading dark:text-eoe-dark-text-heading pl-1">
              Sign up with email
            </label>
            <input
              type="checkbox"
              id="register"
              checked={isRegistering}
              onChange={(e) => {
                setIsRegistering(e.target.checked);
                if (e.target.checked && emailOrUsername.includes('@')) {
                  handleEmailChange({ target: { value: emailOrUsername } } as React.ChangeEvent<HTMLInputElement>);
                }
              }}
              className="toggle-switch-input col-span-1"
            />
          </div>
          {!isRegistering && (
            <>
              <button
                type="button"
                onClick={() => onForgotPassword(emailOrUsername)}
                className="pl-1 text-sm text-eoe-link-blue dark:text-eoe-dark-link-blue hover:underline focus:outline-none"
              >
                Forgot password?
              </button>
              <button
                type="button"
                onClick={() => onUseCode()}
                className="pl-1 text-sm text-eoe-link-blue dark:text-eoe-dark-link-blue hover:underline focus:outline-none"
              >
                Sign in with code
              </button>
            </>
          )}
        </div>

        {isRegistering && (
          <div className="form-control">
            <InputField
              type="username"
              id="username"
              name="username"
              labelText="Username"
              fieldRequired
              value={username}
              onChange={handleUsernameChange}
              validationState={isValidUsername && !error ? 'valid' : username ? 'invalid' : 'none'}
              autoComplete="username"
              error={validationMessage}
              spellCheck={false}
              maxLength={USERNAME_REQUIREMENTS.maxLength}
              minLength={USERNAME_REQUIREMENTS.minLength}
            />
          </div>
        )}

        {isRegistering && (
          <div className="grid grid-flow-col-dense gap-3 items-center justify-between w-48">
            <label htmlFor="register" className="col-span-2 block font-semibold text-sm text-eoe-text-heading dark:text-eoe-dark-text-heading pl-1">
              Private account
            </label>
            <input
              type="checkbox"
              id="register"
              checked={isPrivate}
              onChange={(e) => {
                setIsPrivate(e.target.checked);
              }}
              className="toggle-switch-input col-span-1"
            />
          </div>
        )}

        {error && (
          <div className="flex items-center text-warning-red dark:text-warning-red-dark text-sm">
            <Info className="mr-1 size-4" />
            <span>{error}</span>
          </div>
        )}
      </form>

      <div className="flex flex-grow flex-col pb-8 justify-end place-items-end mb-6 mt-auto space-y-2 sm:space-y-0 sm:space-x-2 sm:flex sm:justify-end sm:flex-row sm:mb-0">
        <button
          type="button"
          onClick={onClose}
          className="btn-secondary w-full sm:w-fit py-2 px-4"
        >
          Cancel
        </button>
        <button
          type="submit"
          onClick={handleSubmit}
          disabled={isButtonDisabled}
          className={`w-full sm:w-fit py-2 px-4 ${isButtonDisabled ? 'btn-primary-disabled' : 'btn-primary'} ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {isSubmitting
            ? isRegistering
              ? 'Signing Up...'
              : 'Logging In...'
            : isRegistering
            ? 'Sign Up'
            : 'Log In'}
        </button>
      </div>
    </div>
  );
};

export default LocalSignin;